import {
  Clock,
  FileText,
  GraduationCap,
  Languages,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Settings,
  UserCheck,
  Users,
  X
} from "lucide-react";
import React, { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../stores/authStore";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navigation = [
    {
      name: "ダッシュボード",
      href: "/dashboard",
      icon: LayoutDashboard,
      current: location.pathname === "/dashboard",
    },
    // 管理者のみ表示
    ...(user?.role === "admin" ? [
      {
        name: "ユーザー管理",
        href: "/users",
        icon: Users,
        current: location.pathname === "/users",
      },
      {
        name: "アプリケーション管理",
        href: "/apps",
        icon: Settings,
        current: location.pathname === "/apps",
      },
    ] : []),
    {
      name: "個人設定",
      href: "/profile",
      icon: UserCheck,
      current: location.pathname === "/profile",
    },
  ];

  const appLinks = [
    {
      name: "履歴助手",
      href: "/api/sso/launch/resume",
      icon: FileText,
      color: "bg-blue-500",
    },
    {
      name: "評価助手",
      href: "/api/sso/launch/evaluation",
      icon: UserCheck,
      color: "bg-green-500",
    },
    {
      name: "面接助手",
      href: "/api/sso/launch/interview",
      icon: Users,
      color: "bg-purple-500",
    },
    {
      name: "案件助手",
      href: "/api/sso/launch/project",
      icon: Clock,
      color: "bg-orange-500",
    },
    {
      name: "e-learning",
      href: "/api/sso/launch/elearning",
      icon: GraduationCap,
      color: "bg-indigo-500",
    },
    {
      name: "WebMail",
      href: "/api/sso/launch/webmail",
      icon: Mail,
      color: "bg-red-500",
    },
    {
      name: "翻訳アシスタント",
      href: "/api/sso/launch/translation",
      icon: Languages,
      color: "bg-cyan-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* モバイルサイドバー */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <img
              src="/brand/arjin-logo-horizontal.svg"
              alt="ARJIN Portal"
              className="h-8 w-auto"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`sidebar-link ${item.current ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setSidebarOpen(false);
                  navigate(item.href);
                }}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* デスクトップサイドバー */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
            <img
              src="/brand/arjin-logo-horizontal.svg"
              alt="ARJIN Portal"
              className="h-8 w-auto"
            />
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`sidebar-link ${item.current ? "active" : ""}`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="lg:pl-64">
        {/* ヘッダー */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex items-center space-x-4">
              {/* アプリケーションクイックアクセス */}
              <div className="hidden md:flex items-center space-x-2">
                {appLinks.map((app) => (
                  <Button
                    key={app.name}
                    variant="ghost"
                    size="sm"
                    className={`h-8 w-8 p-0 hover:${app.color} hover:bg-opacity-10 transition-colors`}
                    title={app.name}
                    onClick={() => window.open(app.href, '_blank')}
                  >
                    <app.icon
                      className={`h-4 w-4 ${app.color.replace("bg-", "text-")}`}
                    />
                  </Button>
                ))}
              </div>

              {/* ユーザーメニュー */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={user?.avatar_url}
                        alt={user?.full_name}
                      />
                      <AvatarFallback>
                        {user?.full_name?.charAt(0) ||
                          user?.username?.charAt(0) ||
                          "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.full_name}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <UserCheck className="mr-2 h-4 w-4" />
                    <span>個人設定</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>ログアウト</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* ページコンテンツ */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
